import { useMutation } from '@apollo/client'
import { DeleteOutlineOutlined } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { ConfirmationDialog } from 'src/components/ConfirmationDialog'
import { graphql } from 'src/gql'

const Delete = graphql(`
  mutation DeleteMemberBenefits($input: DeleteMemberBenefitsInput!) {
    deleteMemberBenefits(input: $input) {
      message
      success
      member {
        ... on Member {
          id
          benefits {
            id
          }
          treatmentProvider {
            id
          }
          rewardsCode {
            id
          }
          rewardsPlan {
            id
          }
        }
        ... on ValidationErrors {
          validationErrors {
            errors
          }
        }
      }
    }
  }
`)

interface Props {
  memberId: number
}

export const RemoveContract: React.FC<Props> = ({ memberId }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(false)

  const [remove] = useMutation(Delete, {
    onCompleted: ({ deleteMemberBenefits: { success, message, member } }) => {
      if (member?.__typename === 'ValidationErrors') {
        const { validationErrors } = member
        message = validationErrors
          .map(({ errors }) => errors)
          .flat()
          .join(', ')
      }
      enqueueSnackbar(message, { variant: success ? 'success' : 'error' })
    },
    onError: () =>
      enqueueSnackbar('Oops. Something went wrong.', { variant: 'error' }),
  })

  const onClose = async (confirmed: boolean) => {
    if (confirmed) remove({ variables: { input: { memberId } } })
    setOpen(false)
  }

  return (
    <>
      <ConfirmationDialog
        open={open}
        title="Are you sure?"
        message="The member will be permanently removed from the contract, and all associated records will be deleted. They will no longer appear in future reports for this contract. They will have access to the free version of the app and can be assigned to a different contract."
        onClose={onClose}
        confirmText="Yes"
      />
      <IconButton
        aria-label="Remove Contract"
        onClick={() => setOpen(true)}
        size="small">
        <DeleteOutlineOutlined />
      </IconButton>
    </>
  )
}
